import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { TabPanel, TabView } from "primereact/tabview";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";

import AIPromptEditor from "../../components/AIPromptEditor/AIPromptEditor";
import BaseRateFilesTable from "./components/BaseRateFilesTable";
import { CaseRateRulesTable } from "./components/CaseRateRulesTable";
import ConfigsTable from "./components/ConfigsTable";
import ContractsTable from "./components/ContractsTable";
import CustomCptVolumeTable from "./components/CustomCptVolumeTable";
import EdiConversionFilesTable from "./components/EdiConversionFilesTable";
import { ExplodingFeeScheduleTable } from "./components/ExplodingFeeScheduleTable";
import { HierarchyRulesManager } from "./components/HierarchyRulesManager";
import { InpatientRulesetsTable } from "./components/InpatientRulesetsTable";
import MarketPricesTable from "./components/MarketPricesTable";
import { PackagesTable } from "./components/PackagesTable";
import PayerRulesTable from "./components/PayerRulesTable";
import PayersTable from "./components/PayersTable";
import {
  CONTRACTS_SUBTAB_CUSTOM_CPT_VOLUME,
  CONTRACTS_SUBTAB_LIBRARY,
  CONTRACTS_TAB_INDEX,
} from "./utils/constants";

const StyledButton = styled(Button)`
  gap: ${sizer(2)};
`;

const FlexWrapper = styled.div`
  display: flex;
  justify-content: end;
  margin-bottom: ${sizer(3)};
`;

const CONTRACTS_SUBTABS = [
  CONTRACTS_SUBTAB_LIBRARY,
  CONTRACTS_SUBTAB_CUSTOM_CPT_VOLUME,
] as const;

export function PayerRules() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabIndex = Number(searchParams.get("tab"));
  const subtab = searchParams.get("subtab") ?? CONTRACTS_SUBTAB_LIBRARY;
  const contractsSubtabIndex = Math.max(
    0,
    CONTRACTS_SUBTABS.indexOf(subtab as (typeof CONTRACTS_SUBTABS)[number])
  );

  const handleContractsSubtabChange = (index: number) => {
    const nextSubtab = CONTRACTS_SUBTABS[index] ?? CONTRACTS_SUBTAB_LIBRARY;
    setSearchParams({
      tab: String(CONTRACTS_TAB_INDEX),
      subtab: nextSubtab,
    });
  };

  return (
    <TabView
      className="w-full"
      activeIndex={activeTabIndex}
      onTabChange={(e) => {
        if (e.index === CONTRACTS_TAB_INDEX) {
          setSearchParams({
            tab: e.index.toString(),
            subtab: CONTRACTS_SUBTABS[contractsSubtabIndex],
          });
        } else {
          setSearchParams({ tab: e.index.toString() });
        }
      }}
    >
      <TabPanel header="MPPR">
        <FlexWrapper>
          <StyledButton
            icon="pi pi-plus"
            className="p-button-raised"
            onClick={() => navigate("/payer-rules/mppr/new")}
          >
            New Payer Rule
          </StyledButton>
        </FlexWrapper>
        <PayerRulesTable />
      </TabPanel>
      <TabPanel header="Case Rate">
        <FlexWrapper>
          <StyledButton
            icon="pi pi-plus"
            className="p-button-raised"
            onClick={() => navigate("/payer-rules/case-rate/new")}
          >
            New Payer Rule
          </StyledButton>
        </FlexWrapper>
        <CaseRateRulesTable />
      </TabPanel>
      <TabPanel header="Packages">
        <FlexWrapper>
          <StyledButton
            icon="pi pi-plus"
            className="p-button-raised"
            onClick={() => navigate("/payer-rules/packages/new")}
          >
            New Package
          </StyledButton>
        </FlexWrapper>
        <PackagesTable />
      </TabPanel>
      <TabPanel header="Exploding Fee Schedules">
        <FlexWrapper>
          <StyledButton
            icon="pi pi-plus"
            className="p-button-raised"
            onClick={() => navigate("/payer-rules/exploding-fee-schedules/new")}
          >
            New Exploding Fee Schedule
          </StyledButton>
        </FlexWrapper>
        <ExplodingFeeScheduleTable />
      </TabPanel>
      <TabPanel header="Inpatient Rulesets">
        <FlexWrapper>
          <StyledButton
            icon="pi pi-plus"
            className="p-button-raised"
            onClick={() => navigate("/payer-rules/inpatient-rulesets/new")}
          >
            New Inpatient Ruleset
          </StyledButton>
        </FlexWrapper>
        <InpatientRulesetsTable />
      </TabPanel>
      <TabPanel header="Payers">
        <FlexWrapper>
          <StyledButton
            icon="pi pi-plus"
            className="p-button-raised"
            onClick={() => navigate("/payer-rules/payer/new")}
          >
            New Payer
          </StyledButton>
        </FlexWrapper>
        <PayersTable />
      </TabPanel>
      <TabPanel header="Base Rate Files">
        <BaseRateFilesTable />
      </TabPanel>
      <TabPanel header="Base Rate Configs">
        <ConfigsTable />
      </TabPanel>
      <TabPanel header="Contracts">
        <TabView
          activeIndex={contractsSubtabIndex}
          onTabChange={(e) => handleContractsSubtabChange(e.index)}
        >
          <TabPanel header="Library">
            <FlexWrapper>
              <StyledButton
                icon="pi pi-plus"
                className="p-button-raised"
                onClick={() => navigate("/payer-rules/contracts/new")}
              >
                New Contract
              </StyledButton>
            </FlexWrapper>
            <ContractsTable />
          </TabPanel>
          <TabPanel header="Custom CPT Volume">
            <CustomCptVolumeTable />
          </TabPanel>
        </TabView>
      </TabPanel>
      <TabPanel header="Market Price Datasets">
        <MarketPricesTable />
      </TabPanel>
      <TabPanel header="EDI Conversion Files">
        <EdiConversionFilesTable />
      </TabPanel>
      <TabPanel header="Hierarchy Rules">
        <FlexWrapper>
          <StyledButton
            icon="pi pi-plus"
            className="p-button-raised"
            onClick={() => navigate("/payer-rules/hierarchy-rules/new")}
          >
            New Hierarchy Ruleset
          </StyledButton>
        </FlexWrapper>
        <HierarchyRulesManager />
      </TabPanel>
      <TabPanel header="Prompt for AI Analysis">
        <AIPromptEditor />
      </TabPanel>
    </TabView>
  );
}
